import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import { Button } from './ui';
import { Icon, type IconName } from './icons';

export interface ConfirmOptions {
  title: string;
  /** One or two sentences saying what will happen. Say the consequence, not the action. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger draws the confirm button as destructive and warns with an icon. */
  tone?: 'default' | 'danger';
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

/**
 * Confirmations, as a promise.
 *
 * `window.confirm` is fine logic and a poor dialog: it is the browser's chrome rather than
 * the application's, it names the origin instead of the product, it cannot be styled or
 * themed, and Chrome offers to suppress it — after which a destructive action would go
 * ahead with no prompt at all.
 *
 * The shape is kept, because the shape was never the problem: `if (await confirm({…}))`
 * reads the same as the call it replaces, so no call site grows a pile of dialog state.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    setRequest(options);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setRequest(null);
    resolver.current?.(ok);
    resolver.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {request && <ConfirmDialog options={request} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm must be used inside a ConfirmProvider.');
  return ask;
}

function ConfirmDialog({
  options, onSettle,
}: { options: ConfirmOptions; onSettle: (ok: boolean) => void }) {
  const panel = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef<Element | null>(null);

  const danger = options.tone === 'danger';

  useEffect(() => {
    restoreFocus.current = document.activeElement;

    // Cancel takes focus, not confirm. A dialog that deletes on a stray Enter is worse
    // than one that needs a deliberate second press.
    cancelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onSettle(false); return; }

      // A dialog is modal, so focus stays inside it — otherwise the next Tab lands on the
      // page behind, which is still there and still looks clickable.
      if (e.key !== 'Tab') return;

      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable?.length) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      (restoreFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [onSettle]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/45 px-4 pt-[18vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onSettle(false); }}
    >
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ff-confirm-title"
        aria-describedby={options.body ? 'ff-confirm-body' : undefined}
        className="w-full max-w-[420px] rounded-lg border border-[var(--ff-border)]
                   bg-[var(--ff-surface)] p-5 shadow-[var(--ff-shadow-lg)]"
      >
        <div className="flex gap-3.5">
          <span className={clsx(
            'mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full',
            danger
              ? 'bg-[var(--ff-danger-soft)] text-[var(--ff-danger)]'
              : 'bg-[var(--ff-surface-sunken)] text-[var(--ff-text-muted)]',
          )}>
            <Icon name={(danger ? 'alert' : 'info') as IconName} size={17} />
          </span>

          <div className="min-w-0">
            <h2 id="ff-confirm-title" className="text-[15px] font-semibold leading-snug">
              {options.title}
            </h2>
            {options.body && (
              <div id="ff-confirm-body"
                   className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--ff-text-muted)]">
                {options.body}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancelRef} onClick={() => onSettle(false)}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={danger ? 'dangerSolid' : 'primary'}
            onClick={() => onSettle(true)}
          >
            {options.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
}
